import { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Alert,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormHelperText,
    CircularProgress,
    Divider,
    Tooltip
} from '@mui/material';
import ConfirmModal from '../../components/modals/common/ConfirmModal';
import ConfirmDeleteModal from '../../components/modals/common/ConfirmDeleteModal';

const RETENTION_OPTIONS = [
    { value: 180, label: '6 months' },
    { value: 365, label: '1 year' },
    { value: 730, label: '2 years' },
    { value: 1825, label: '5 years' },
    { value: 0, label: 'Never (keep forever)' }
];

function EventViewerArchiveConfigSection({
    config,
    onChange,
    onSave,
    hasChanges,
    localisation,
    onArchiveNow,
    onArchiveImmediately,
    onPurgeColdArchive,
    onDeleteColdArchive,
    archiveLoading,
    archiveImmediatelyLoading,
    purgeLoading,
    deleteLoading
}) {
    const [archiveNowConfirmOpen, setArchiveNowConfirmOpen] = useState(false);
    const [archiveImmediatelyConfirmOpen, setArchiveImmediatelyConfirmOpen] = useState(false);
    const [purgeConfirmOpen, setPurgeConfirmOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    const isAnyLoading = archiveLoading || archiveImmediatelyLoading || purgeLoading || deleteLoading;

    const formatDate = (isoDate) => {
        if (!isoDate) return 'Never';
        const date = new Date(isoDate);
        if (isNaN(date.getTime())) return 'Invalid date';
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    const handleArchiveNowConfirm = () => {
        setArchiveNowConfirmOpen(false);
        onArchiveNow();
    };

    const handleArchiveImmediatelyConfirm = () => {
        setArchiveImmediatelyConfirmOpen(false);
        onArchiveImmediately();
    };

    const handlePurgeConfirm = () => {
        setPurgeConfirmOpen(false);
        onPurgeColdArchive();
    };

    const handleDeleteConfirm = () => {
        setDeleteConfirmOpen(false);
        onDeleteColdArchive();
    };

    const archiveDays = config.archive_after_days || 60;
    const retentionDays = config.retention_days !== undefined ? config.retention_days : 0;
    const retentionLabel = RETENTION_OPTIONS.find(o => o.value === retentionDays)?.label || 'Never (keep forever)';

    return (
        <Box sx={{ textAlign: 'left' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 500 }}>
                Event Viewer Archive
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
                Archiving moves old event viewer entries to a cold archive table to maintain event viewer performance.
                Archived entries can be automatically or manually purged based on a configurable retention period.
            </Alert>

            {/* ===== EVENT VIEWER ARCHIVE ===== */}
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                Event Viewer Archive
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Archive After</InputLabel>
                        <Select
                            value={archiveDays}
                            label="Archive After"
                            onChange={(e) => onChange('archive_after_days', e.target.value)}
                        >
                            <MenuItem value={30}>30 days</MenuItem>
                            <MenuItem value={60}>60 days</MenuItem>
                            <MenuItem value={120}>120 days</MenuItem>
                        </Select>
                        <FormHelperText>
                            Records older than this will be moved to the cold archive
                        </FormHelperText>
                    </FormControl>
                </Grid>
            </Grid>

            <Box sx={{ mt: 3, mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                    <strong>Last archive:</strong> {formatDate(config.last_archive_date)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    <strong>Next scheduled archive:</strong> {formatDate(config.next_archive_date)}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Tooltip title={`Archive only records older than ${archiveDays} days`} arrow>
                        <span>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => setArchiveNowConfirmOpen(true)}
                                disabled={isAnyLoading}
                                startIcon={archiveLoading ? <CircularProgress size={20} color="inherit" /> : null}
                                sx={{ minWidth: 180 }}
                            >
                                {archiveLoading ? 'Archiving...' : 'Archive Old Records'}
                            </Button>
                        </span>
                    </Tooltip>
                    <Typography variant="body2" color="text.secondary">
                        Older than {archiveDays} days
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Tooltip title="Archive ALL records in the event viewer regardless of their age" arrow>
                        <span>
                            <Button
                                variant="outlined"
                                color="warning"
                                onClick={() => setArchiveImmediatelyConfirmOpen(true)}
                                disabled={isAnyLoading}
                                startIcon={archiveImmediatelyLoading ? <CircularProgress size={20} color="inherit" /> : null}
                                sx={{ minWidth: 180 }}
                            >
                                {archiveImmediatelyLoading ? 'Archiving...' : 'Archive All Records'}
                            </Button>
                        </span>
                    </Tooltip>
                    <Typography variant="body2" color="text.secondary">
                        Regardless of date
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* ===== COLD ARCHIVE MANAGEMENT ===== */}
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                Cold Archive Management
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Delete Archived Records After</InputLabel>
                        <Select
                            value={retentionDays}
                            label="Delete Archived Records After"
                            onChange={(e) => onChange('retention_days', e.target.value)}
                        >
                            {RETENTION_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>
                            Archived records older than this will be permanently deleted
                        </FormHelperText>
                    </FormControl>
                </Grid>
            </Grid>

            <Box sx={{ mt: 3, mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                    <strong>Last purge:</strong> {formatDate(config.last_purge_date)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    <strong>Next scheduled purge:</strong> {retentionDays === 0 ? 'Disabled (retention set to Never)' : formatDate(config.next_purge_date)}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Tooltip title={retentionDays === 0
                        ? 'Retention is set to "Never" - change the retention period above to use this option'
                        : `Permanently delete archived records older than ${retentionLabel}`} arrow>
                        <span>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => setPurgeConfirmOpen(true)}
                                disabled={isAnyLoading || retentionDays === 0}
                                startIcon={purgeLoading ? <CircularProgress size={20} color="inherit" /> : null}
                                sx={{ minWidth: 180 }}
                            >
                                {purgeLoading ? 'Purging...' : 'Delete Old Archives'}
                            </Button>
                        </span>
                    </Tooltip>
                    <Typography variant="body2" color="text.secondary">
                        {retentionDays === 0
                            ? 'Disabled - set a retention period above to enable'
                            : `Older than ${retentionLabel}`}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Tooltip title="Permanently delete ALL records from the cold archive regardless of their age - this cannot be undone" arrow>
                        <span>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => setDeleteConfirmOpen(true)}
                                disabled={isAnyLoading}
                                startIcon={deleteLoading ? <CircularProgress size={20} color="inherit" /> : null}
                                sx={{ minWidth: 180 }}
                            >
                                {deleteLoading ? 'Deleting...' : 'Delete All Archives'}
                            </Button>
                        </span>
                    </Tooltip>
                    <Typography variant="body2" color="text.secondary">
                        Regardless of age
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Button
                variant="contained"
                color="primary"
                onClick={onSave}
                disabled={!hasChanges}
            >
                {localisation?.["save_button"]?.element_lang || "Save"}
            </Button>

            {/* Confirmation Modal: Archive Old Records */}
            <ConfirmModal
                isOpen={archiveNowConfirmOpen}
                mainTitle="Archive Old Records"
                mainMessage={`This will archive all event viewer records that are older than ${archiveDays} days.\n\nRecords will be moved to the cold archive and removed from the main event viewer. Do you want to proceed?`}
                textConfirm="Archive Old Records"
                textCancel="Cancel"
                onConfirm={handleArchiveNowConfirm}
                onCancel={() => setArchiveNowConfirmOpen(false)}
            />

            {/* Confirmation Modal: Archive All Records */}
            <ConfirmModal
                isOpen={archiveImmediatelyConfirmOpen}
                mainTitle="Archive All Records"
                mainMessage="This will archive ALL event viewer records regardless of their age. This includes recent records from today. All records will be moved to the cold archive and the event viewer will be cleared. Are you sure you want to archive everything?"
                textConfirm="Archive Everything"
                textCancel="Cancel"
                onConfirm={handleArchiveImmediatelyConfirm}
                onCancel={() => setArchiveImmediatelyConfirmOpen(false)}
            />

            {/* Confirmation Modal: Purge Old Archives */}
            <ConfirmModal
                isOpen={purgeConfirmOpen}
                mainTitle="Delete Old Archived Records"
                mainMessage={`This will permanently delete all archived records that have been in the cold archive for longer than ${retentionLabel}.\n\nThis action cannot be undone. Deleted records cannot be recovered. Do you want to proceed?`}
                textConfirm="Delete Old Archives"
                textCancel="Cancel"
                onConfirm={handlePurgeConfirm}
                onCancel={() => setPurgeConfirmOpen(false)}
            />

            {/* Confirmation Modal: Delete All Archives */}
            <ConfirmDeleteModal
                isOpen={deleteConfirmOpen}
                entityType="cold archive"
                entityName="All Archived Event Viewer Data"
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteConfirmOpen(false)}
                loading={deleteLoading}
                message="Are you sure you want to permanently delete ALL archived event viewer data? This will remove every entry from the cold archive table regardless of age. This action cannot be undone."
            />
        </Box>
    );
}

export default EventViewerArchiveConfigSection;
